/**
 * Marketing site shell — shared theme context, NavBar and Footer used by every
 * public marketing page (Home, Contact). Extracted from the homepage so a
 * second page can reuse the identical dark/light theme, nav and footer
 * without duplicating ~150 lines of JSX that would drift on the next edit.
 */
import { useState, useEffect, useMemo, useCallback, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Sun, Moon, Shield, Menu, X } from 'lucide-react';

/* ── theme ── */
export type Theme = 'dark' | 'light';
export const ThemeCtx = createContext<{ theme: Theme; toggle: () => void }>({
  theme: 'dark', toggle: () => {},
});
export const useTheme = () => useContext(ThemeCtx);

/**
 * Every top-level marketing page does `const [theme,setTheme]=useState(...);
 * const toggle=()=>...; <ThemeCtx.Provider value={{theme,toggle}}>` — a fresh
 * `{theme,toggle}` object literal on every single render, which re-renders
 * EVERY consumer of useTheme() (NavBar, Footer, every section) whenever the
 * page re-renders for any reason, not just when the theme actually changes.
 * This hook is the fix, centralized: stable `toggle` reference (useCallback)
 * + a value object only recreated when theme itself changes (useMemo).
 */
export function useThemeState(defaultTheme: Theme = 'dark') {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);
  return useMemo(() => ({ theme, toggle }), [theme, toggle]);
}

export const THEMES = {
  dark: {
    bg: '#0D1117',
    fg: '#E6EDF3',
    card: '#161B22',
    card2: '#1C2128',
    muted: '#21262D',
    mutedFg: '#8B949E',
    border: 'rgba(48,54,61,0.8)',
    borderHover: 'rgba(37,211,102,0.4)',
    green: '#3DDC84',
    greenDim: 'rgba(61,220,132,0.08)',
    greenDim2: 'rgba(61,220,132,0.14)',
    greenGlow: 'rgba(61,220,132,0.22)',
    navBg: 'rgba(13,17,23,0.92)',
    shadow: '0 4px 32px rgba(0,0,0,0.4)',
    textSecondary: '#CDD9E5',
    divider: 'rgba(48,54,61,0.6)',
    chartStroke: '#3DDC84',
    chartFill: 'rgba(61,220,132,0.12)',
  },
  light: {
    bg: '#F4FFF8',
    fg: '#0A1A0F',
    card: '#FFFFFF',
    card2: '#E9FBF0',
    muted: '#D4F5E3',
    mutedFg: '#2D6B45',
    border: 'rgba(37,211,102,0.25)',
    borderHover: 'rgba(37,211,102,0.6)',
    green: '#25D366',
    greenDim: 'rgba(37,211,102,0.1)',
    greenDim2: 'rgba(37,211,102,0.18)',
    greenGlow: 'rgba(37,211,102,0.35)',
    navBg: 'rgba(244,255,248,0.92)',
    shadow: '0 4px 40px rgba(37,211,102,0.15)',
    textSecondary: '#1A4D30',
    divider: 'rgba(37,211,102,0.12)',
    chartStroke: '#25D366',
    chartFill: 'rgba(37,211,102,0.12)',
  },
};

const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Product', href: '#features' },
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Resources', href: '#faq' },
  { label: 'Blog', href: '#use-cases' },
];

/**
 * Scroll-to-section links only work natively when already on the page that
 * has the target id. From any other page (e.g. Contact) a plain `#hash`
 * click does nothing — so this navigates to `/` first when needed, and lets
 * Home's own hash-scroll effect (in pages/home/index.tsx) finish the job
 * once that section has mounted.
 */
function useHashScroll() {
  const navigate = useNavigate();
  const location = useLocation();
  return (hash: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const id = hash.slice(1);
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/${hash}`);
    }
  };
}

export function NavBar() {
  const { theme, toggle } = useTheme();
  const t = THEMES[theme];
  const navigate = useNavigate();
  const scrollTo = useHashScroll();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const goLogin = (e: React.MouseEvent) => { e.preventDefault(); setMobileOpen(false); navigate('/login'); };
  const goHome = (e: React.MouseEvent) => { e.preventDefault(); setMobileOpen(false); navigate('/'); };
  const goContact = (e: React.MouseEvent) => { e.preventDefault(); setMobileOpen(false); navigate('/contact'); };
  const goSignup = (e: React.MouseEvent) => { e.preventDefault(); setMobileOpen(false); navigate('/signup'); };
  const scrollToMobile = (hash: string) => (e: React.MouseEvent) => { scrollTo(hash)(e); setMobileOpen(false); };

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
      background: scrolled ? t.navBg : 'transparent',
      backdropFilter: scrolled ? 'blur(20px)' : undefined,
      borderBottom: scrolled ? `1px solid ${t.border}` : 'none',
      transition: 'all 0.3s',
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>

        {/* Logo */}
        <a href="/" onClick={goHome} style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, textDecoration: 'none', cursor: 'pointer' }}>
          <img src="/logo.png" alt="Baatly" style={{ width: 32, height: 32, objectFit: 'contain' }} />
          <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, color: t.fg, fontSize: 20, letterSpacing: '-0.5px' }}>
            Baat<span style={{ color: t.green }}>ly</span>
          </span>
        </a>

        {/* Centre nav — hidden on mobile */}
        <nav style={{ alignItems: 'center', gap: 28 }} className="home-nav-center">
          {NAV_LINKS.map((l) => (
            <a key={l.label} href={`/${l.href}`} onClick={scrollTo(l.href)}
              style={{ fontSize: 14, fontWeight: 500, color: t.mutedFg, textDecoration: 'none', transition: 'color 0.2s', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.color = t.fg)}
              onMouseLeave={e => (e.currentTarget.style.color = t.mutedFg)}>
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right group — theme toggle + Sign in / Request a Demo / Get Started */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={toggle}
            style={{ width: 36, height: 36, borderRadius: 10, background: t.greenDim, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }}>
            {theme === 'dark' ? <Sun size={16} color={t.green} /> : <Moon size={16} color={t.green} />}
          </button>
          <a href="/login" onClick={goLogin} style={{ fontSize: 14, fontWeight: 500, color: t.mutedFg, textDecoration: 'none', cursor: 'pointer' }} className="home-nav-login">Sign in</a>
          <a href="/contact" onClick={goContact} className="home-nav-demo"
            style={{ padding: '8px 16px', borderRadius: 10, background: 'transparent', border: `1px solid ${t.border}`, color: t.fg, fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            Request a Demo
          </a>
          <a href="/signup" onClick={goSignup}
            style={{ padding: '8px 16px', borderRadius: 10, background: t.green, color: theme === 'dark' ? '#0D1117' : '#fff', fontSize: 13, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', cursor: 'pointer' }}>
            Get Started <ArrowRight size={12} />
          </a>
          <button onClick={() => setMobileOpen(o => !o)} className="home-nav-toggle"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            style={{ width: 36, height: 36, borderRadius: 10, background: t.greenDim, border: `1px solid ${t.border}`, alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            {mobileOpen ? <X size={18} color={t.green} /> : <Menu size={18} color={t.green} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown — Product/Features/Pricing/Resources/Blog have no other
          way to be reached below the 768px breakpoint where .home-nav-center hides. */}
      {mobileOpen && (
        <div className="home-nav-mobile-panel" style={{
          flexDirection: 'column', gap: 2, padding: '8px 24px 20px',
          background: t.navBg, backdropFilter: 'blur(20px)', borderBottom: `1px solid ${t.border}`,
        }}>
          {NAV_LINKS.map((l) => (
            <a key={l.label} href={`/${l.href}`} onClick={scrollToMobile(l.href)}
              style={{ padding: '12px 4px', fontSize: 15, fontWeight: 500, color: t.mutedFg, textDecoration: 'none', borderBottom: `1px solid ${t.divider}` }}>
              {l.label}
            </a>
          ))}
          <a href="/login" onClick={goLogin} style={{ padding: '12px 4px', fontSize: 15, fontWeight: 500, color: t.mutedFg, textDecoration: 'none', borderBottom: `1px solid ${t.divider}` }}>
            Sign in
          </a>
          <a href="/contact" onClick={goContact} style={{ padding: '12px 4px', fontSize: 15, fontWeight: 500, color: t.mutedFg, textDecoration: 'none', borderBottom: `1px solid ${t.divider}` }}>
            Request a Demo
          </a>
          <a href="/signup" onClick={goSignup} style={{ padding: '12px 4px', fontSize: 15, fontWeight: 700, color: t.green, textDecoration: 'none' }}>
            Get Started
          </a>
        </div>
      )}

      <style>{`
        .home-nav-center { display: none; }
        @media (min-width: 768px) { .home-nav-center { display: flex; } }
        .home-nav-login { display: none; }
        @media (min-width: 640px) { .home-nav-login { display: block; } }
        .home-nav-demo { display: none; }
        @media (min-width: 640px) { .home-nav-demo { display: block; } }
        .home-nav-toggle { display: flex; }
        @media (min-width: 768px) { .home-nav-toggle { display: none; } }
        .home-nav-mobile-panel { display: flex; }
        @media (min-width: 768px) { .home-nav-mobile-panel { display: none !important; } }
      `}</style>
    </header>
  );
}

export function Footer() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const navigate = useNavigate();
  const scrollTo = useHashScroll();
  const goPrivacy = (e: React.MouseEvent) => { e.preventDefault(); navigate('/privacy-policy'); };
  const goContact = (e: React.MouseEvent) => { e.preventDefault(); navigate('/contact'); };

  const cols: Array<{ title: string; links: Array<{ label: string; href: string; onClick?: (e: React.MouseEvent) => void }> }> = [
    {
      title: 'Product',
      links: [
        { label: 'Broadcast', href: '#features', onClick: scrollTo('#features') },
        { label: 'Automation', href: '#features', onClick: scrollTo('#features') },
        { label: 'AI Chatbot', href: '#features', onClick: scrollTo('#features') },
        { label: 'Analytics', href: '#features', onClick: scrollTo('#features') },
        { label: 'Payments', href: '#features', onClick: scrollTo('#features') },
        { label: 'Integrations', href: '#features', onClick: scrollTo('#features') },
      ],
    },
    { title: 'Solutions', links: [{ label: 'E-commerce', href: '#' }, { label: 'Education', href: '#' }, { label: 'Healthcare', href: '#' }, { label: 'Real Estate', href: '#' }, { label: 'Finance', href: '#' }, { label: 'Travel', href: '#' }] },
    { title: 'Resources', links: [{ label: 'Blog', href: '#use-cases', onClick: scrollTo('#use-cases') }, { label: 'Case Studies', href: '#' }, { label: 'Docs', href: '#' }, { label: 'API Reference', href: '#' }, { label: 'Changelog', href: '#' }, { label: 'Status', href: '#' }] },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '#' },
        { label: 'Careers', href: '#' },
        { label: 'Partners', href: '#' },
        { label: 'Press', href: '#' },
        { label: 'Contact', href: '/contact', onClick: goContact },
        { label: 'Privacy Policy', href: '/privacy-policy', onClick: goPrivacy },
      ],
    },
  ];

  return (
    <footer style={{ background: t.card, borderTop: `1px solid ${t.border}`, padding: '72px 24px 32px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr repeat(4, 1fr)', gap: 48, marginBottom: 60 }} className="footer-grid">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <img src="/logo.png" alt="Baatly" style={{ width: 32, height: 32, objectFit: 'contain' }} />
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, color: t.fg, fontSize: 20 }}>
                Baat<span style={{ color: t.green }}>ly</span>
              </span>
            </div>
            <p style={{ color: t.mutedFg, fontSize: 13.5, lineHeight: 1.75, marginBottom: 18 }}>
              The #1 Official WhatsApp Marketing Platform. Powered by WhatsApp Business API.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['G4.8 ★', 'Capterra 4.9', 'PH #1'].map((b) => (
                <span key={b} style={{ fontSize: 10, fontWeight: 700, color: t.green, background: t.greenDim, border: `1px solid ${t.border}`, padding: '4px 10px', borderRadius: 8 }}>{b}</span>
              ))}
            </div>
          </div>
          {cols.map(({ title, links }) => (
            <div key={title}>
              <h4 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, color: t.fg, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>{title}</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {links.map((l) => (
                  <li key={l.label}><a href={l.href.startsWith('#') ? `/${l.href}` : l.href} onClick={l.onClick} style={{ color: t.mutedFg, fontSize: 13.5, textDecoration: 'none', transition: 'color 0.2s', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.color = t.green)}
                    onMouseLeave={e => (e.currentTarget.style.color = t.mutedFg)}>{l.label}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ borderTop: `1px solid ${t.divider}`, paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ color: t.mutedFg, fontSize: 13, margin: 0 }}>© 2025 Baatly. All rights reserved.</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: t.mutedFg, fontSize: 12 }}>
            <Shield size={12} color={t.green} />
            Official Meta Business Partner ·
            <span style={{ color: t.green, fontWeight: 700 }}>WhatsApp BSP</span>
          </div>
        </div>
      </div>
      <style>{`.footer-grid { @media (max-width: 900px) { grid-template-columns: 1fr 1fr !important; } } @media (max-width: 600px) { .footer-grid { grid-template-columns: 1fr !important; } }`}</style>
    </footer>
  );
}
